import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import {
  getProgramReportCard,
  getProgramNavigation,
  type ProgramReportCardData,
  type ProgramNavigationData
} from '../services/rpc';

/**
 * React Query hook to fetch comprehensive report card data for a program
 * Includes all 7 program-level tables in one call
 */
export function useProgramReportCard(
  baciCode: string | null | undefined,
  options?: {
    enabled?: boolean;
    staleTime?: number;
    gcTime?: number;
  }
): UseQueryResult<ProgramReportCardData, Error> {
  const {
    enabled = true,
    staleTime = 5 * 60 * 1000, // 5 minutes - report card data is relatively static
    gcTime = 10 * 60 * 1000 // 10 minutes cache
  } = options || {};

  return useQuery<ProgramReportCardData, Error>({
    queryKey: ['program-report-card', baciCode],
    queryFn: () => {
      if (!baciCode) {
        throw new Error('BACI code is required');
      }
      return getProgramReportCard(baciCode);
    },
    enabled: enabled && Boolean(baciCode),
    staleTime,
    gcTime,
    // Report card can be cached longer
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: 1000
  });
}

/**
 * React Query hook to fetch alphabetical navigation data
 */
export function useProgramNavigation(
  currentBaciCode: string | null | undefined,
  options?: {
    enabled?: boolean;
  }
): UseQueryResult<ProgramNavigationData, Error> {
  const { enabled = true } = options || {};

  return useQuery<ProgramNavigationData, Error>({
    queryKey: ['program-navigation', currentBaciCode],
    queryFn: () => {
      if (!currentBaciCode) {
        throw new Error('Current BACI code is required');
      }
      return getProgramNavigation(currentBaciCode);
    },
    enabled: enabled && Boolean(currentBaciCode),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    retry: 1
  });
}

export default useProgramReportCard;
